import React, { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import {
  ArrowLeft, Plus, Trash2, Save, X, Globe, Layout, FileText, Image, Upload,
  Briefcase, Link as LinkIcon, Eye, EyeOff, GripVertical, ChevronRight,
  ShoppingCart, Package, Truck, Palette, Cpu, Settings, Instagram, Linkedin, Mail,
  Twitter, Facebook, Youtube, Github, ExternalLink, Shield, FileEdit, Search,
  PenTool, Copy
} from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ICON_MAP = {
  ShoppingCart, Package, Truck, Palette, Cpu, Settings, Globe, Briefcase, Image
};

const SOCIAL_ICONS = { instagram: Instagram, linkedin: Linkedin, email: Mail, twitter: Twitter, facebook: Facebook, youtube: Youtube, github: Github };

// Reusable Image Upload Component
const ImageUploadField = ({ value, onChange, label, token }) => {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await axios.post(`${API}/site-content/upload-image`, formData, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" }
      });
      const url = `${BACKEND_URL}${res.data.url}`;
      onChange(url);
      toast.success("Bild hochgeladen!");
    } catch (err) { toast.error("Upload fehlgeschlagen"); }
    setUploading(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  return (
    <div className="space-y-2">
      {label && <label className="block text-sm text-mmg-silver-chalice">{label}</label>}
      <div className="flex gap-2">
        <input type="text" value={value || ""} onChange={(e) => onChange(e.target.value)}
          placeholder="https://... oder hochladen" className="flex-1 py-3 px-4 bg-black border border-mmg-mine-shaft text-white" />
        <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading}
          className="px-4 py-3 bg-mmg-mine-shaft text-white hover:bg-white hover:text-black transition-colors disabled:opacity-50 flex items-center gap-2"
          data-testid="upload-image-btn">
          <Upload size={16} />{uploading ? "..." : "Upload"}
        </button>
        <input ref={fileRef} type="file" accept="image/*" onChange={handleUpload} className="hidden" />
      </div>
      {value && (
        <div className="mt-2 relative w-32 h-20 bg-mmg-mine-shaft border border-mmg-mine-shaft overflow-hidden">
          <img src={value} alt="Preview" className="w-full h-full object-cover" onError={(e) => e.target.style.display = "none"} />
        </div>
      )}
    </div>
  );
};

const LANGS = ["de", "en", "ar"];
const LANG_LABELS = { de: "Deutsch", en: "English", ar: "العربية" };

// Reusable multilingual text input
const MultiLangInput = ({ value = {}, onChange, label, textarea = false, rows = 3 }) => {
  const [activeLang, setActiveLang] = useState("de");
  const Tag = textarea ? "textarea" : "input";
  return (
    <div className="space-y-2">
      {label && <label className="block text-sm text-mmg-silver-chalice">{label}</label>}
      <div className="flex gap-1 mb-1">
        {LANGS.map(l => (
          <button key={l} type="button" onClick={() => setActiveLang(l)}
            className={`px-3 py-1 text-xs font-heading uppercase ${activeLang === l ? "bg-white text-black" : "bg-mmg-mine-shaft text-mmg-silver-chalice hover:text-white"}`}>
            {l.toUpperCase()}
          </button>
        ))}
      </div>
      <Tag
        value={value[activeLang] || ""}
        onChange={(e) => onChange({ ...value, [activeLang]: e.target.value })}
        {...(textarea ? { rows } : { type: "text" })}
        className={`w-full py-3 px-4 bg-black border border-mmg-mine-shaft text-white font-body ${textarea ? "resize-none" : ""}`}
        dir={activeLang === "ar" ? "rtl" : "ltr"}
      />
    </div>
  );
};

// Section cards for the hub
const SECTIONS = [
  { key: "hero", label: { de: "Hero-Bereich", en: "Hero Section" }, icon: Layout, desc: { de: "Überschrift, Tagline, Button", en: "Headline, tagline, button" } },
  { key: "about", label: { de: "Über uns", en: "About" }, icon: FileText, desc: { de: "Mission-Text und Bild", en: "Mission text and image" } },
  { key: "services", label: { de: "Leistungen", en: "Services" }, icon: Briefcase, desc: { de: "Services hinzufügen/bearbeiten/löschen", en: "Add/edit/delete services" } },
  { key: "portfolio", label: { de: "Portfolio", en: "Portfolio" }, icon: Image, desc: { de: "Projekte verwalten", en: "Manage projects" } },
  { key: "navigation", label: { de: "Navigation", en: "Navigation" }, icon: Globe, desc: { de: "Menüpunkte im Header", en: "Header menu items" } },
  { key: "footer", label: { de: "Footer", en: "Footer" }, icon: LinkIcon, desc: { de: "Social Media, Links, Texte", en: "Social media, links, texts" } },
  { key: "contact", label: { de: "Kontakt", en: "Contact" }, icon: Mail, desc: { de: "Kontaktbereich-Texte", en: "Contact section texts" } },
  { key: "legal", label: { de: "Rechtliches", en: "Legal Pages" }, icon: Shield, desc: { de: "Impressum & Datenschutz", en: "Imprint & Privacy Policy" } },
  { key: "customPages", label: { de: "Eigene Seiten", en: "Custom Pages" }, icon: FileEdit, desc: { de: "Benutzerdefinierte Seiten", en: "User-defined pages" } },
  { key: "seo", label: { de: "SEO & Meta", en: "SEO & Meta" }, icon: Search, desc: { de: "SEO, Favicon, Meta-Tags", en: "SEO, favicon, meta tags" } },
];

// =================== SECTION EDITORS ===================

const HeroEditor = ({ data, onSave, token }) => {
  const [form, setForm] = useState(data);
  return (
    <div className="space-y-6">
      <MultiLangInput label="Tagline" value={form.tagline} onChange={(v) => setForm({ ...form, tagline: v })} />
      <MultiLangInput label="Headline" value={form.headline} onChange={(v) => setForm({ ...form, headline: v })} textarea rows={2} />
      <MultiLangInput label="Button-Text (CTA)" value={form.cta} onChange={(v) => setForm({ ...form, cta: v })} />
      <ImageUploadField label="Hintergrundbild" value={form.background_image} onChange={(v) => setForm({ ...form, background_image: v })} token={token} />
      <button onClick={() => onSave(form)} className="px-6 py-3 bg-white text-black font-heading font-bold uppercase hover:bg-black hover:text-white border border-white" data-testid="save-hero-btn">
        <Save size={16} className="inline mr-2" />Speichern
      </button>
    </div>
  );
};

const AboutEditor = ({ data, onSave, token }) => {
  const [form, setForm] = useState(data);
  return (
    <div className="space-y-6">
      <MultiLangInput label="Titel" value={form.title} onChange={(v) => setForm({ ...form, title: v })} />
      <MultiLangInput label="Beschreibung" value={form.description} onChange={(v) => setForm({ ...form, description: v })} textarea rows={5} />
      <ImageUploadField label="Bild" value={form.image} onChange={(v) => setForm({ ...form, image: v })} token={token} />
      <button onClick={() => onSave(form)} className="px-6 py-3 bg-white text-black font-heading font-bold uppercase hover:bg-black hover:text-white border border-white" data-testid="save-about-btn">
        <Save size={16} className="inline mr-2" />Speichern
      </button>
    </div>
  );
};

const ContactEditor = ({ data, onSave }) => {
  const [form, setForm] = useState(data);
  return (
    <div className="space-y-6">
      <MultiLangInput label="Titel" value={form.title} onChange={(v) => setForm({ ...form, title: v })} />
      <MultiLangInput label="Untertitel" value={form.subtitle} onChange={(v) => setForm({ ...form, subtitle: v })} />
      <button onClick={() => onSave(form)} className="px-6 py-3 bg-white text-black font-heading font-bold uppercase hover:bg-black hover:text-white border border-white" data-testid="save-contact-btn">
        <Save size={16} className="inline mr-2" />Speichern
      </button>
    </div>
  );
};

const ICON_OPTIONS = ["ShoppingCart", "Package", "Truck", "Palette", "Cpu", "Settings", "Globe", "Briefcase", "Image"];

const ServicesEditor = ({ data, onSave }) => {
  const [form, setForm] = useState(data);
  const addItem = () => {
    const items = [...(form.items || []), { id: crypto.randomUUID(), title: { de: "", en: "", ar: "" }, desc: { de: "", en: "", ar: "" }, icon: "Settings" }];
    setForm({ ...form, items });
  };
  const removeItem = (id) => setForm({ ...form, items: form.items.filter(i => i.id !== id) });
  const updateItem = (id, field, value) => {
    setForm({ ...form, items: form.items.map(i => i.id === id ? { ...i, [field]: value } : i) });
  };
  return (
    <div className="space-y-6">
      <MultiLangInput label="Abschnittstitel" value={form.title} onChange={(v) => setForm({ ...form, title: v })} />
      <div className="flex items-center justify-between">
        <h3 className="font-heading text-lg text-white">Leistungen ({form.items?.length || 0})</h3>
        <button onClick={addItem} className="px-3 py-2 bg-white text-black font-heading font-bold uppercase text-sm hover:bg-black hover:text-white border border-white" data-testid="add-service-btn">
          <Plus size={14} className="inline mr-1" />Hinzufügen
        </button>
      </div>
      {(form.items || []).map((item, idx) => (
        <div key={item.id} className="bg-mmg-cod-gray border border-mmg-mine-shaft p-4 space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-mmg-silver-chalice text-sm font-heading">#{idx + 1}</span>
            <button onClick={() => removeItem(item.id)} className="text-mmg-silver-chalice hover:text-red-500" data-testid={`delete-service-${idx}`}><Trash2 size={16} /></button>
          </div>
          <MultiLangInput label="Titel" value={item.title} onChange={(v) => updateItem(item.id, "title", v)} />
          <MultiLangInput label="Beschreibung" value={item.desc} onChange={(v) => updateItem(item.id, "desc", v)} />
          <div>
            <label className="block text-sm text-mmg-silver-chalice mb-1">Icon</label>
            <select value={item.icon} onChange={(e) => updateItem(item.id, "icon", e.target.value)}
              className="py-2 px-3 bg-black border border-mmg-mine-shaft text-white text-sm">
              {ICON_OPTIONS.map(ic => <option key={ic} value={ic}>{ic}</option>)}
            </select>
          </div>
        </div>
      ))}
      <button onClick={() => onSave(form)} className="px-6 py-3 bg-white text-black font-heading font-bold uppercase hover:bg-black hover:text-white border border-white" data-testid="save-services-btn">
        <Save size={16} className="inline mr-2" />Speichern
      </button>
    </div>
  );
};

const PortfolioEditor = ({ data, onSave, token }) => {
  const [form, setForm] = useState(data);
  const addItem = () => {
    const items = [...(form.items || []), { id: crypto.randomUUID(), name: "", category: { de: "", en: "", ar: "" }, logo: "", url: "#" }];
    setForm({ ...form, items });
  };
  const removeItem = (id) => setForm({ ...form, items: form.items.filter(i => i.id !== id) });
  const updateItem = (id, field, value) => {
    setForm({ ...form, items: form.items.map(i => i.id === id ? { ...i, [field]: value } : i) });
  };
  return (
    <div className="space-y-6">
      <MultiLangInput label="Abschnittstitel" value={form.title} onChange={(v) => setForm({ ...form, title: v })} />
      <MultiLangInput label="Beschreibung" value={form.description} onChange={(v) => setForm({ ...form, description: v })} textarea rows={3} />
      <div className="flex items-center justify-between">
        <h3 className="font-heading text-lg text-white">Projekte ({form.items?.length || 0})</h3>
        <button onClick={addItem} className="px-3 py-2 bg-white text-black font-heading font-bold uppercase text-sm hover:bg-black hover:text-white border border-white" data-testid="add-portfolio-btn">
          <Plus size={14} className="inline mr-1" />Hinzufügen
        </button>
      </div>
      {(form.items || []).map((item, idx) => (
        <div key={item.id} className="bg-mmg-cod-gray border border-mmg-mine-shaft p-4 space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-mmg-silver-chalice text-sm font-heading">#{idx + 1}</span>
            <button onClick={() => removeItem(item.id)} className="text-mmg-silver-chalice hover:text-red-500" data-testid={`delete-portfolio-${idx}`}><Trash2 size={16} /></button>
          </div>
          <div>
            <label className="block text-sm text-mmg-silver-chalice mb-1">Name</label>
            <input type="text" value={item.name || ""} onChange={(e) => updateItem(item.id, "name", e.target.value)}
              className="w-full py-3 px-4 bg-black border border-mmg-mine-shaft text-white" />
          </div>
          <MultiLangInput label="Kategorie" value={item.category || {}} onChange={(v) => updateItem(item.id, "category", v)} />
          <ImageUploadField label="Logo" value={item.logo} onChange={(v) => updateItem(item.id, "logo", v)} token={token} />
          <div>
            <label className="block text-sm text-mmg-silver-chalice mb-1">Website URL</label>
            <input type="text" value={item.url || ""} onChange={(e) => updateItem(item.id, "url", e.target.value)}
              placeholder="https://..." className="w-full py-3 px-4 bg-black border border-mmg-mine-shaft text-white" />
          </div>
        </div>
      ))}
      <button onClick={() => onSave(form)} className="px-6 py-3 bg-white text-black font-heading font-bold uppercase hover:bg-black hover:text-white border border-white" data-testid="save-portfolio-btn">
        <Save size={16} className="inline mr-2" />Speichern
      </button>
    </div>
  );
};

const NavigationEditor = ({ data, onSave }) => {
  const [form, setForm] = useState(data);
  const addItem = () => {
    const items = [...(form.items || []), { id: crypto.randomUUID(), label: { de: "", en: "", ar: "" }, href: "#", visible: true }];
    setForm({ ...form, items });
  };
  const removeItem = (id) => setForm({ ...form, items: form.items.filter(i => i.id !== id) });
  const updateItem = (id, field, value) => {
    setForm({ ...form, items: form.items.map(i => i.id === id ? { ...i, [field]: value } : i) });
  };
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="font-heading text-lg text-white">Menüpunkte ({form.items?.length || 0})</h3>
        <button onClick={addItem} className="px-3 py-2 bg-white text-black font-heading font-bold uppercase text-sm hover:bg-black hover:text-white border border-white" data-testid="add-nav-btn">
          <Plus size={14} className="inline mr-1" />Hinzufügen
        </button>
      </div>
      {(form.items || []).map((item, idx) => (
        <div key={item.id} className="bg-mmg-cod-gray border border-mmg-mine-shaft p-4 space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-mmg-silver-chalice text-sm font-heading">#{idx + 1}</span>
            <div className="flex items-center gap-2">
              <button onClick={() => updateItem(item.id, "visible", !item.visible)}
                className={`p-1 ${item.visible ? "text-green-400" : "text-mmg-silver-chalice"}`} data-testid={`toggle-nav-${idx}`}>
                {item.visible ? <Eye size={16} /> : <EyeOff size={16} />}
              </button>
              <button onClick={() => removeItem(item.id)} className="text-mmg-silver-chalice hover:text-red-500" data-testid={`delete-nav-${idx}`}><Trash2 size={16} /></button>
            </div>
          </div>
          <MultiLangInput label="Bezeichnung" value={item.label} onChange={(v) => updateItem(item.id, "label", v)} />
          <div>
            <label className="block text-sm text-mmg-silver-chalice mb-1">Link (href)</label>
            <input type="text" value={item.href || ""} onChange={(e) => updateItem(item.id, "href", e.target.value)}
              placeholder="#about, /brand, https://..." className="w-full py-3 px-4 bg-black border border-mmg-mine-shaft text-white" />
            <p className="text-xs text-mmg-silver-chalice mt-1">#about = Abschnitt auf Hauptseite | /brand = Unterseite | https://... = Externer Link</p>
          </div>
        </div>
      ))}
      <button onClick={() => onSave(form)} className="px-6 py-3 bg-white text-black font-heading font-bold uppercase hover:bg-black hover:text-white border border-white" data-testid="save-nav-btn">
        <Save size={16} className="inline mr-2" />Speichern
      </button>
    </div>
  );
};

const SOCIAL_OPTIONS = ["instagram", "linkedin", "email", "twitter", "facebook", "youtube", "github"];

const FooterEditor = ({ data, onSave }) => {
  const [form, setForm] = useState(data);
  const addSocial = () => {
    const social_links = [...(form.social_links || []), { id: crypto.randomUUID(), platform: "instagram", url: "", visible: true }];
    setForm({ ...form, social_links });
  };
  const removeSocial = (id) => setForm({ ...form, social_links: form.social_links.filter(i => i.id !== id) });
  const updateSocial = (id, field, value) => {
    setForm({ ...form, social_links: form.social_links.map(i => i.id === id ? { ...i, [field]: value } : i) });
  };
  const addLink = () => {
    const links = [...(form.links || []), { id: crypto.randomUUID(), label: { de: "", en: "", ar: "" }, href: "/", visible: true }];
    setForm({ ...form, links });
  };
  const removeLink = (id) => setForm({ ...form, links: form.links.filter(i => i.id !== id) });
  const updateLink = (id, field, value) => {
    setForm({ ...form, links: form.links.map(i => i.id === id ? { ...i, [field]: value } : i) });
  };

  return (
    <div className="space-y-6">
      <MultiLangInput label="Tagline" value={form.tagline} onChange={(v) => setForm({ ...form, tagline: v })} />
      <MultiLangInput label="Copyright-Text" value={form.copyright} onChange={(v) => setForm({ ...form, copyright: v })} />

      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-heading text-lg text-white">Social Media Links</h3>
          <button onClick={addSocial} className="px-3 py-2 bg-white text-black font-heading font-bold uppercase text-sm hover:bg-black hover:text-white border border-white" data-testid="add-social-btn">
            <Plus size={14} className="inline mr-1" />Hinzufügen
          </button>
        </div>
        {(form.social_links || []).map((item, idx) => (
          <div key={item.id} className="bg-mmg-cod-gray border border-mmg-mine-shaft p-4 flex items-center gap-4">
            <select value={item.platform} onChange={(e) => updateSocial(item.id, "platform", e.target.value)}
              className="py-2 px-3 bg-black border border-mmg-mine-shaft text-white text-sm">
              {SOCIAL_OPTIONS.map(p => <option key={p} value={p}>{p}</option>)}
            </select>
            <input type="text" value={item.url} onChange={(e) => updateSocial(item.id, "url", e.target.value)}
              placeholder="URL" className="flex-1 py-2 px-3 bg-black border border-mmg-mine-shaft text-white text-sm" />
            <button onClick={() => updateSocial(item.id, "visible", !item.visible)}
              className={item.visible ? "text-green-400" : "text-mmg-silver-chalice"}>{item.visible ? <Eye size={16} /> : <EyeOff size={16} />}</button>
            <button onClick={() => removeSocial(item.id)} className="text-mmg-silver-chalice hover:text-red-500"><Trash2 size={16} /></button>
          </div>
        ))}
      </div>

      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-heading text-lg text-white">Footer-Links</h3>
          <button onClick={addLink} className="px-3 py-2 bg-white text-black font-heading font-bold uppercase text-sm hover:bg-black hover:text-white border border-white" data-testid="add-footer-link-btn">
            <Plus size={14} className="inline mr-1" />Hinzufügen
          </button>
        </div>
        {(form.links || []).map((item, idx) => (
          <div key={item.id} className="bg-mmg-cod-gray border border-mmg-mine-shaft p-4 space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-mmg-silver-chalice text-sm">#{idx + 1}</span>
              <div className="flex items-center gap-2">
                <button onClick={() => updateLink(item.id, "visible", !item.visible)}
                  className={item.visible ? "text-green-400" : "text-mmg-silver-chalice"}>{item.visible ? <Eye size={16} /> : <EyeOff size={16} />}</button>
                <button onClick={() => removeLink(item.id)} className="text-mmg-silver-chalice hover:text-red-500"><Trash2 size={16} /></button>
              </div>
            </div>
            <MultiLangInput label="Bezeichnung" value={item.label} onChange={(v) => updateLink(item.id, "label", v)} />
            <div>
              <label className="block text-sm text-mmg-silver-chalice mb-1">Link (href)</label>
              <input type="text" value={item.href || ""} onChange={(e) => updateLink(item.id, "href", e.target.value)}
                className="w-full py-3 px-4 bg-black border border-mmg-mine-shaft text-white" />
            </div>
          </div>
        ))}
      </div>

      <button onClick={() => onSave(form)} className="px-6 py-3 bg-white text-black font-heading font-bold uppercase hover:bg-black hover:text-white border border-white" data-testid="save-footer-btn">
        <Save size={16} className="inline mr-2" />Speichern
      </button>
    </div>
  );
};

// =================== LEGAL PAGES EDITOR ===================

const LegalEditor = ({ token, lang }) => {
  const [activePage, setActivePage] = useState(null); // "impressum" or "privacy"
  const [impressum, setImpressum] = useState({ content: {}, meta_title: {}, meta_description: {} });
  const [privacy, setPrivacy] = useState({ content: {}, meta_title: {}, meta_description: {} });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchLegal();
  }, []);

  const fetchLegal = async () => {
    try {
      const [impRes, privRes] = await Promise.all([
        axios.get(`${API}/legal/impressum`),
        axios.get(`${API}/legal/privacy`)
      ]);
      setImpressum(impRes.data);
      setPrivacy(privRes.data);
    } catch (e) { }
    setLoading(false);
  };

  const handleSave = async (type) => {
    setSaving(true);
    try {
      const data = type === "impressum" ? impressum : privacy;
      await axios.put(`${API}/legal/${type}`, data, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(lang === "de" ? "Gespeichert!" : "Saved!");
    } catch (e) { toast.error("Error saving"); }
    setSaving(false);
  };

  if (loading) return <div className="text-mmg-silver-chalice">{lang === "de" ? "Laden..." : "Loading..."}</div>;

  // Page selection
  if (!activePage) {
    return (
      <div className="space-y-6">
        <h2 className="font-heading font-bold text-2xl text-white">{lang === "de" ? "Rechtliche Seiten" : "Legal Pages"}</h2>
        <p className="text-mmg-silver-chalice">{lang === "de" ? "Bearbeiten Sie Impressum und Datenschutzerklärung" : "Edit imprint and privacy policy"}</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <button onClick={() => setActivePage("impressum")}
            className="bg-mmg-cod-gray border border-mmg-mine-shaft p-6 text-left hover:border-white/30 transition-colors group"
            data-testid="edit-impressum-btn">
            <div className="flex items-center gap-3 mb-3">
              <Shield size={20} className="text-mmg-silver-chalice group-hover:text-white" />
              <h3 className="font-heading text-lg text-white">{lang === "de" ? "Impressum" : "Imprint"}</h3>
            </div>
            <p className="text-sm text-mmg-silver-chalice">{lang === "de" ? "Gesetzlich vorgeschriebene Angaben" : "Legally required information"}</p>
            <ChevronRight size={16} className="text-mmg-silver-chalice mt-3 group-hover:text-white" />
          </button>
          <button onClick={() => setActivePage("privacy")}
            className="bg-mmg-cod-gray border border-mmg-mine-shaft p-6 text-left hover:border-white/30 transition-colors group"
            data-testid="edit-privacy-btn">
            <div className="flex items-center gap-3 mb-3">
              <FileText size={20} className="text-mmg-silver-chalice group-hover:text-white" />
              <h3 className="font-heading text-lg text-white">{lang === "de" ? "Datenschutz" : "Privacy Policy"}</h3>
            </div>
            <p className="text-sm text-mmg-silver-chalice">{lang === "de" ? "Datenschutzerklärung nach DSGVO" : "GDPR-compliant privacy policy"}</p>
            <ChevronRight size={16} className="text-mmg-silver-chalice mt-3 group-hover:text-white" />
          </button>
        </div>
      </div>
    );
  }

  // Edit page
  const data = activePage === "impressum" ? impressum : privacy;
  const setData = activePage === "impressum" ? setImpressum : setPrivacy;
  const title = activePage === "impressum" ? (lang === "de" ? "Impressum" : "Imprint") : (lang === "de" ? "Datenschutz" : "Privacy Policy");

  return (
    <div className="space-y-6">
      <button onClick={() => setActivePage(null)} className="flex items-center gap-2 text-mmg-silver-chalice hover:text-white">
        <ArrowLeft size={18} />{lang === "de" ? "Zurück" : "Back"}
      </button>
      <h2 className="font-heading font-bold text-2xl text-white">{title}</h2>
      
      <MultiLangInput label={lang === "de" ? "Meta-Titel (SEO)" : "Meta Title (SEO)"} value={data.meta_title || {}} onChange={(v) => setData({ ...data, meta_title: v })} />
      <MultiLangInput label={lang === "de" ? "Meta-Beschreibung (SEO)" : "Meta Description (SEO)"} value={data.meta_description || {}} onChange={(v) => setData({ ...data, meta_description: v })} />
      <MultiLangInput label={lang === "de" ? "Inhalt (HTML erlaubt)" : "Content (HTML allowed)"} value={data.content || {}} onChange={(v) => setData({ ...data, content: v })} textarea rows={15} />
      
      <button onClick={() => handleSave(activePage)} disabled={saving}
        className="px-6 py-3 bg-white text-black font-heading font-bold uppercase hover:bg-black hover:text-white border border-white disabled:opacity-50"
        data-testid="save-legal-btn">
        <Save size={16} className="inline mr-2" />{saving ? "..." : (lang === "de" ? "Speichern" : "Save")}
      </button>
    </div>
  );
};

// =================== CUSTOM PAGES EDITOR ===================

const CustomPagesEditor = ({ token, lang }) => {
  const [pages, setPages] = useState([]);
  const [editPage, setEditPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchPages(); }, []);

  const fetchPages = async () => {
    try {
      const res = await axios.get(`${API}/custom-pages`, { headers: { Authorization: `Bearer ${token}` } });
      setPages(res.data);
    } catch (e) { }
    setLoading(false);
  };

  const handleNew = () => {
    setEditPage({
      isNew: true,
      slug: "",
      title: { de: "", en: "", ar: "" },
      content: { de: "", en: "", ar: "" },
      meta_title: { de: "", en: "", ar: "" },
      meta_description: { de: "", en: "", ar: "" },
      published: false,
      show_in_navigation: false,
      show_in_footer: false
    });
  };

  const handleSave = async () => {
    if (!editPage.slug) {
      toast.error(lang === "de" ? "Slug erforderlich" : "Slug required");
      return;
    }
    setSaving(true);
    try {
      if (editPage.isNew) {
        await axios.post(`${API}/custom-pages`, editPage, { headers: { Authorization: `Bearer ${token}` } });
      } else {
        await axios.put(`${API}/custom-pages/${editPage.id}`, editPage, { headers: { Authorization: `Bearer ${token}` } });
      }
      toast.success(lang === "de" ? "Gespeichert!" : "Saved!");
      setEditPage(null);
      fetchPages();
    } catch (e) { 
      toast.error(e.response?.data?.detail || "Error saving"); 
    }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm(lang === "de" ? "Seite wirklich löschen?" : "Really delete page?")) return;
    try {
      await axios.delete(`${API}/custom-pages/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(lang === "de" ? "Gelöscht!" : "Deleted!");
      fetchPages();
    } catch (e) { toast.error("Error deleting"); }
  };

  if (loading) return <div className="text-mmg-silver-chalice">{lang === "de" ? "Laden..." : "Loading..."}</div>;

  // Edit mode
  if (editPage) {
    return (
      <div className="space-y-6">
        <button onClick={() => setEditPage(null)} className="flex items-center gap-2 text-mmg-silver-chalice hover:text-white">
          <ArrowLeft size={18} />{lang === "de" ? "Zurück" : "Back"}
        </button>
        <h2 className="font-heading font-bold text-2xl text-white">{editPage.isNew ? (lang === "de" ? "Neue Seite" : "New Page") : (lang === "de" ? "Seite bearbeiten" : "Edit Page")}</h2>
        
        <div>
          <label className="block text-sm text-mmg-silver-chalice mb-1">URL-Slug*</label>
          <input type="text" value={editPage.slug} onChange={(e) => setEditPage({ ...editPage, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-") })}
            placeholder="meine-seite" className="w-full py-3 px-4 bg-black border border-mmg-mine-shaft text-white" />
          <p className="text-xs text-mmg-silver-chalice mt-1">{lang === "de" ? "Wird zur URL: /page/meine-seite" : "Becomes URL: /page/my-page"}</p>
        </div>

        <MultiLangInput label={lang === "de" ? "Titel" : "Title"} value={editPage.title} onChange={(v) => setEditPage({ ...editPage, title: v })} />
        <MultiLangInput label={lang === "de" ? "Inhalt (HTML erlaubt)" : "Content (HTML allowed)"} value={editPage.content} onChange={(v) => setEditPage({ ...editPage, content: v })} textarea rows={10} />
        <MultiLangInput label={lang === "de" ? "Meta-Titel (SEO)" : "Meta Title (SEO)"} value={editPage.meta_title || {}} onChange={(v) => setEditPage({ ...editPage, meta_title: v })} />
        <MultiLangInput label={lang === "de" ? "Meta-Beschreibung (SEO)" : "Meta Description (SEO)"} value={editPage.meta_description || {}} onChange={(v) => setEditPage({ ...editPage, meta_description: v })} />

        <div className="flex flex-wrap gap-6">
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={editPage.published} onChange={(e) => setEditPage({ ...editPage, published: e.target.checked })}
              className="w-5 h-5 bg-black border border-mmg-mine-shaft" />
            <span className="text-white">{lang === "de" ? "Veröffentlicht" : "Published"}</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={editPage.show_in_navigation} onChange={(e) => setEditPage({ ...editPage, show_in_navigation: e.target.checked })}
              className="w-5 h-5 bg-black border border-mmg-mine-shaft" />
            <span className="text-white">{lang === "de" ? "In Navigation zeigen" : "Show in navigation"}</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={editPage.show_in_footer} onChange={(e) => setEditPage({ ...editPage, show_in_footer: e.target.checked })}
              className="w-5 h-5 bg-black border border-mmg-mine-shaft" />
            <span className="text-white">{lang === "de" ? "Im Footer zeigen" : "Show in footer"}</span>
          </label>
        </div>

        <button onClick={handleSave} disabled={saving}
          className="px-6 py-3 bg-white text-black font-heading font-bold uppercase hover:bg-black hover:text-white border border-white disabled:opacity-50"
          data-testid="save-custom-page-btn">
          <Save size={16} className="inline mr-2" />{saving ? "..." : (lang === "de" ? "Speichern" : "Save")}
        </button>
      </div>
    );
  }

  // List view
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-heading font-bold text-2xl text-white">{lang === "de" ? "Eigene Seiten" : "Custom Pages"}</h2>
          <p className="text-mmg-silver-chalice text-sm mt-1">{lang === "de" ? "Erstellen Sie benutzerdefinierte Seiten wie AGB, FAQ usw." : "Create custom pages like Terms, FAQ, etc."}</p>
        </div>
        <button onClick={handleNew} className="px-4 py-2 bg-white text-black font-heading font-bold uppercase text-sm hover:bg-black hover:text-white border border-white" data-testid="new-custom-page-btn">
          <Plus size={14} className="inline mr-1" />{lang === "de" ? "Neue Seite" : "New Page"}
        </button>
      </div>

      {pages.length === 0 ? (
        <p className="text-mmg-silver-chalice text-center py-8">{lang === "de" ? "Noch keine Seiten erstellt" : "No pages created yet"}</p>
      ) : (
        <div className="space-y-2">
          {pages.map((page) => (
            <div key={page.id} className="bg-mmg-cod-gray border border-mmg-mine-shaft p-4 flex items-center justify-between">
              <div className="flex-1">
                <p className="font-heading text-white">{page.title?.de || page.title?.en || page.slug}</p>
                <p className="text-xs text-mmg-silver-chalice">/page/{page.slug}</p>
              </div>
              <div className="flex items-center gap-4">
                {page.published ? (
                  <span className="text-green-400 text-xs flex items-center gap-1"><Eye size={12} /> {lang === "de" ? "Aktiv" : "Active"}</span>
                ) : (
                  <span className="text-mmg-silver-chalice text-xs flex items-center gap-1"><EyeOff size={12} /> {lang === "de" ? "Entwurf" : "Draft"}</span>
                )}
                <button onClick={() => setEditPage(page)} className="text-mmg-silver-chalice hover:text-white p-1" data-testid={`edit-page-${page.slug}`}>
                  <PenTool size={16} />
                </button>
                <button onClick={() => handleDelete(page.id)} className="text-mmg-silver-chalice hover:text-red-500 p-1" data-testid={`delete-page-${page.slug}`}>
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// =================== SEO SETTINGS EDITOR ===================

// Tooltip/Help component for explanations
const HelpTooltip = ({ text }) => (
  <div className="group relative inline-block ml-2">
    <span className="text-mmg-silver-chalice hover:text-white cursor-help">
      <svg className="w-4 h-4 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <circle cx="12" cy="12" r="10" strokeWidth="2" />
        <path strokeWidth="2" d="M9.09 9a3 3 0 015.83 1c0 2-3 3-3 3" />
        <path strokeWidth="2" d="M12 17h.01" />
      </svg>
    </span>
    <div className="absolute z-50 invisible group-hover:visible bg-mmg-mine-shaft text-white text-xs p-3 rounded-lg w-64 -left-24 top-6 shadow-lg border border-white/10">
      {text}
    </div>
  </div>
);

const SEOEditor = ({ token, lang }) => {
  const [settings, setSettings] = useState({
    site_title: {},
    site_description: {},
    keywords: {},
    og_image: "",
    favicon_url: "",
    apple_touch_icon_url: "",
    google_analytics_id: "",
    google_tag_manager_id: "",
    robots_txt: "",
    faq_schema: [],
    author_info: {},
    speakable_content: { cssSelector: ".speakable, h1, h2" },
    apple_mobile_web_app_title: "",
    apple_mobile_web_app_capable: true
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [seoScore, setSeoScore] = useState(null);
  const [activeTab, setActiveTab] = useState('basic'); // basic, social, analytics, ai
  const faviconRef = useRef(null);
  const ogImageRef = useRef(null);
  const appleTouchIconRef = useRef(null);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchSettings(); fetchSeoScore(); }, []);

  const fetchSettings = async () => {
    try {
      const res = await axios.get(`${API}/seo-settings`);
      setSettings({ ...settings, ...res.data });
    } catch (e) { }
    setLoading(false);
  };

  const fetchSeoScore = async () => {
    try {
      const res = await axios.get(`${API}/seo-analysis`);
      setSeoScore(res.data);
    } catch (e) { }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/seo-settings`, settings, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(lang === "de" ? "SEO-Einstellungen gespeichert!" : "SEO settings saved!");
      await fetchSeoScore();
    } catch (e) { toast.error("Error saving"); }
    setSaving(false);
  };

  const handleFaviconUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await axios.post(`${API}/seo-settings/upload-favicon`, formData, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" }
      });
      setSettings({ ...settings, favicon_url: `${BACKEND_URL}${res.data.url}` });
      toast.success(lang === "de" ? "Favicon hochgeladen!" : "Favicon uploaded!");
    } catch (e) { toast.error("Upload failed"); }
  };

  const handleAppleTouchIconUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await axios.post(`${API}/seo-settings/upload-apple-icon`, formData, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" }
      });
      setSettings({ ...settings, apple_touch_icon_url: `${BACKEND_URL}${res.data.url}` });
      toast.success(lang === "de" ? "Apple Icon hochgeladen!" : "Apple Icon uploaded!");
    } catch (e) { toast.error("Upload failed"); }
  };

  const handleOGImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await axios.post(`${API}/site-content/upload-image`, formData, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" }
      });
      setSettings({ ...settings, og_image: `${BACKEND_URL}${res.data.url}` });
      toast.success(lang === "de" ? "OG-Bild hochgeladen!" : "OG image uploaded!");
    } catch (e) { toast.error("Upload failed"); }
  };

  const addFaq = () => {
    setSettings({
      ...settings,
      faq_schema: [...(settings.faq_schema || []), { question: "", answer: "" }]
    });
  };

  const removeFaq = (index) => {
    const newFaqs = [...settings.faq_schema];
    newFaqs.splice(index, 1);
    setSettings({ ...settings, faq_schema: newFaqs });
  };

  const updateFaq = (index, field, value) => {
    const newFaqs = [...settings.faq_schema];
    newFaqs[index][field] = value;
    setSettings({ ...settings, faq_schema: newFaqs });
  };

  if (loading) return <div className="text-mmg-silver-chalice">{lang === "de" ? "Laden..." : "Loading..."}</div>;

  // Tab configuration with simplified German labels
  const tabs = [
    { id: 'basic', label: lang === "de" ? "Grundlagen" : "Basics", icon: "📝" },
    { id: 'social', label: lang === "de" ? "Social Media" : "Social Media", icon: "📱" },
    { id: 'analytics', label: lang === "de" ? "Analyse" : "Analytics", icon: "📊" },
    { id: 'ai', label: lang === "de" ? "KI & Sprachsuche" : "AI & Voice", icon: "🤖" },
  ];

  return (
    <div className="space-y-6">
      {/* Header with Score */}
      <div className="flex items-start justify-between">
        <div>
          <h2 className="font-heading font-bold text-2xl text-white">
            {lang === "de" ? "Suchmaschinenoptimierung (SEO)" : "Search Engine Optimization (SEO)"}
          </h2>
          <p className="text-mmg-silver-chalice text-sm mt-1">
            {lang === "de" 
              ? "Verbessern Sie, wie Ihre Website bei Google und anderen Suchmaschinen gefunden wird." 
              : "Improve how your website is found on Google and other search engines."}
          </p>
        </div>
        {seoScore && (
          <div className="text-right bg-mmg-cod-gray border border-mmg-mine-shaft p-3 rounded">
            <div className="text-2xl font-bold text-white">{seoScore.ai_seo_score}/100</div>
            <div className={`text-xs px-2 py-1 mt-1 inline-block ${seoScore.ai_seo_score >= 75 ? 'bg-green-600' : seoScore.ai_seo_score >= 50 ? 'bg-yellow-600' : 'bg-red-600'} text-white rounded`}>
              {lang === "de" ? "SEO-Bewertung" : "SEO Score"}
            </div>
          </div>
        )}
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-2 border-b border-mmg-mine-shaft pb-2">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2 text-sm font-heading transition-colors ${
              activeTab === tab.id 
                ? 'bg-white text-black' 
                : 'text-mmg-silver-chalice hover:text-white'
            }`}
            data-testid={`seo-tab-${tab.id}`}
          >
            <span className="mr-2">{tab.icon}</span>
            {tab.label}
          </button>
        ))}
      </div>

      {/* === TAB: GRUNDLAGEN (Basic) === */}
      {activeTab === 'basic' && (
        <div className="space-y-6">
          {/* Website Title & Description */}
          <div className="bg-mmg-cod-gray border border-mmg-mine-shaft p-6 space-y-6">
            <div>
              <h3 className="font-heading text-lg text-white mb-1">
                {lang === "de" ? "Website-Titel & Beschreibung" : "Site Title & Description"}
              </h3>
              <p className="text-xs text-mmg-silver-chalice">
                {lang === "de" 
                  ? "Diese Texte erscheinen in den Google-Suchergebnissen." 
                  : "These texts appear in Google search results."}
              </p>
            </div>
            
            <MultiLangInput 
              label={lang === "de" ? "Website-Titel (erscheint im Browser-Tab)" : "Site Title (appears in browser tab)"} 
              value={settings.site_title || {}} 
              onChange={(v) => setSettings({ ...settings, site_title: v })} 
            />
            
            <div>
              <MultiLangInput 
                label={lang === "de" ? "Kurzbeschreibung für Google" : "Short description for Google"} 
                value={settings.site_description || {}} 
                onChange={(v) => setSettings({ ...settings, site_description: v })} 
                textarea 
                rows={3} 
              />
              <p className="text-xs text-mmg-silver-chalice mt-1">
                {lang === "de" 
                  ? "Tipp: Ideal sind 150-160 Zeichen. Beschreiben Sie kurz, was Ihre Website bietet." 
                  : "Tip: Ideal is 150-160 characters. Briefly describe what your website offers."}
              </p>
            </div>
            
            <MultiLangInput 
              label={
                <span>
                  {lang === "de" ? "Suchbegriffe (Keywords)" : "Search Terms (Keywords)"}
                  <HelpTooltip text={lang === "de" 
                    ? "Geben Sie Begriffe ein, unter denen Sie gefunden werden möchten. Trennen Sie sie mit Kommas, z.B.: Webdesign, Medienagentur, Hamburg" 
                    : "Enter terms you want to be found for. Separate with commas, e.g.: Web design, Media agency, Hamburg"} 
                  />
                </span>
              } 
              value={settings.keywords || {}} 
              onChange={(v) => setSettings({ ...settings, keywords: v })} 
            />
          </div>

          {/* Favicon & Icons */}
          <div className="bg-mmg-cod-gray border border-mmg-mine-shaft p-6 space-y-6">
            <div>
              <h3 className="font-heading text-lg text-white mb-1">
                {lang === "de" ? "Website-Symbol (Favicon)" : "Website Icon (Favicon)"}
              </h3>
              <p className="text-xs text-mmg-silver-chalice">
                {lang === "de" 
                  ? "Das kleine Symbol, das im Browser-Tab neben Ihrem Titel erscheint." 
                  : "The small icon that appears in the browser tab next to your title."}
              </p>
            </div>
            
            <div className="flex items-center gap-6">
              <div className="w-20 h-20 bg-black border-2 border-dashed border-mmg-mine-shaft flex items-center justify-center rounded">
                {settings.favicon_url ? (
                  <img src={settings.favicon_url} alt="Favicon" className="max-w-full max-h-full" onError={(e) => e.target.style.display = "none"} />
                ) : (
                  <span className="text-mmg-silver-chalice text-xs text-center">{lang === "de" ? "Kein Bild" : "No image"}</span>
                )}
              </div>
              <div className="flex-1">
                <button onClick={() => faviconRef.current?.click()}
                  className="px-4 py-2 bg-white text-black font-heading font-bold uppercase text-sm hover:bg-black hover:text-white border border-white"
                  data-testid="upload-favicon-btn">
                  <Upload size={14} className="inline mr-2" />
                  {lang === "de" ? "Bild hochladen" : "Upload Image"}
                </button>
                <p className="text-xs text-mmg-silver-chalice mt-2">
                  {lang === "de" ? "Erlaubte Formate: .ico, .png, .svg (32x32 Pixel empfohlen)" : "Allowed formats: .ico, .png, .svg (32x32 pixels recommended)"}
                </p>
                <input ref={faviconRef} type="file" accept=".ico,.png,.svg" onChange={handleFaviconUpload} className="hidden" />
              </div>
            </div>

            {/* Apple Touch Icon - simplified */}
            <div className="pt-4 border-t border-mmg-mine-shaft">
              <div className="mb-3">
                <h4 className="text-white text-sm font-semibold">
                  {lang === "de" ? "iPhone/iPad App-Symbol" : "iPhone/iPad App Icon"}
                  <HelpTooltip text={lang === "de" 
                    ? "Wenn jemand Ihre Website auf dem iPhone als App speichert, wird dieses Bild als Symbol verwendet." 
                    : "When someone saves your website as an app on iPhone, this image is used as the icon."} 
                  />
                </h4>
              </div>
              
              <div className="flex items-center gap-6">
                <div className="w-20 h-20 bg-black border-2 border-dashed border-mmg-mine-shaft rounded-xl flex items-center justify-center overflow-hidden">
                  {settings.apple_touch_icon_url ? (
                    <img src={settings.apple_touch_icon_url} alt="Apple Icon" className="w-full h-full object-cover" />
                  ) : (
                    <span className="text-mmg-silver-chalice text-xs text-center">{lang === "de" ? "Kein Bild" : "No image"}</span>
                  )}
                </div>
                <div className="flex-1">
                  <button onClick={() => appleTouchIconRef.current?.click()}
                    className="px-4 py-2 bg-white text-black font-heading font-bold uppercase text-sm hover:bg-black hover:text-white border border-white">
                    <Upload size={14} className="inline mr-2" />
                    {lang === "de" ? "Bild hochladen" : "Upload Image"}
                  </button>
                  <p className="text-xs text-mmg-silver-chalice mt-2">
                    {lang === "de" ? "Format: .png (180x180 Pixel empfohlen)" : "Format: .png (180x180 pixels recommended)"}
                  </p>
                  <input ref={appleTouchIconRef} type="file" accept=".png" onChange={handleAppleTouchIconUpload} className="hidden" />
                </div>
              </div>

              {/* Simplified iOS Settings */}
              <div className="mt-4 space-y-3">
                <div>
                  <label className="block text-sm text-mmg-silver-chalice mb-1">
                    {lang === "de" ? "App-Name (max. 12 Zeichen)" : "App Name (max. 12 characters)"}
                  </label>
                  <input type="text" value={settings.apple_mobile_web_app_title || ""} 
                    onChange={(e) => setSettings({ ...settings, apple_mobile_web_app_title: e.target.value })}
                    placeholder="Mahzod" 
                    className="w-full max-w-xs py-2 px-3 bg-black border border-mmg-mine-shaft text-white text-sm" 
                    maxLength="12" 
                  />
                </div>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" 
                    checked={settings.apple_mobile_web_app_capable !== false} 
                    onChange={(e) => setSettings({ ...settings, apple_mobile_web_app_capable: e.target.checked })} 
                    className="h-4 w-4" 
                  />
                  <span className="text-sm text-mmg-silver-chalice">
                    {lang === "de" ? "Website kann als App gespeichert werden" : "Website can be saved as an app"}
                  </span>
                </label>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* === TAB: SOCIAL MEDIA === */}
      {activeTab === 'social' && (
        <div className="space-y-6">
          <div className="bg-mmg-cod-gray border border-mmg-mine-shaft p-6 space-y-6">
            <div>
              <h3 className="font-heading text-lg text-white mb-1">
                {lang === "de" ? "Vorschaubild für Social Media" : "Social Media Preview Image"}
              </h3>
              <p className="text-xs text-mmg-silver-chalice">
                {lang === "de" 
                  ? "Dieses Bild erscheint, wenn jemand Ihre Website auf Facebook, LinkedIn oder WhatsApp teilt." 
                  : "This image appears when someone shares your website on Facebook, LinkedIn or WhatsApp."}
              </p>
            </div>

            <div className="flex items-start gap-6">
              <div className="w-48 h-28 bg-black border-2 border-dashed border-mmg-mine-shaft rounded flex items-center justify-center overflow-hidden">
                {settings.og_image ? (
                  <img src={settings.og_image} alt="Preview" className="w-full h-full object-cover" onError={(e) => e.target.style.display = "none"} />
                ) : (
                  <span className="text-mmg-silver-chalice text-xs text-center px-2">
                    {lang === "de" ? "Kein Vorschaubild" : "No preview image"}
                  </span>
                )}
              </div>
              <div className="flex-1">
                <button onClick={() => ogImageRef.current?.click()}
                  className="px-4 py-2 bg-white text-black font-heading font-bold uppercase text-sm hover:bg-black hover:text-white border border-white"
                  data-testid="upload-og-image-btn">
                  <Upload size={14} className="inline mr-2" />
                  {lang === "de" ? "Bild hochladen" : "Upload Image"}
                </button>
                <p className="text-xs text-mmg-silver-chalice mt-2">
                  {lang === "de" 
                    ? "Empfohlene Größe: 1200 x 630 Pixel (Querformat)" 
                    : "Recommended size: 1200 x 630 pixels (landscape)"}
                </p>
                <input ref={ogImageRef} type="file" accept="image/*" onChange={handleOGImageUpload} className="hidden" />
              </div>
            </div>

            {/* Preview mockup */}
            {settings.og_image && (
              <div className="mt-4 p-4 bg-black rounded border border-mmg-mine-shaft">
                <p className="text-xs text-mmg-silver-chalice mb-2">{lang === "de" ? "Vorschau (ungefähr so sieht es beim Teilen aus):" : "Preview (approximately how it looks when shared):"}</p>
                <div className="bg-white rounded overflow-hidden max-w-md">
                  <img src={settings.og_image} alt="Preview" className="w-full h-32 object-cover" />
                  <div className="p-3">
                    <p className="text-xs text-gray-500 uppercase">{window.location.hostname}</p>
                    <p className="text-black font-semibold text-sm">{settings.site_title?.de || settings.site_title?.en || "Ihr Website-Titel"}</p>
                    <p className="text-gray-600 text-xs line-clamp-2">{settings.site_description?.de || settings.site_description?.en || "Ihre Website-Beschreibung"}</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* === TAB: ANALYTICS === */}
      {activeTab === 'analytics' && (
        <div className="space-y-6">
          <div className="bg-mmg-cod-gray border border-mmg-mine-shaft p-6 space-y-6">
            <div>
              <h3 className="font-heading text-lg text-white mb-1">
                {lang === "de" ? "Besucherstatistiken mit Google" : "Visitor Statistics with Google"}
              </h3>
              <p className="text-xs text-mmg-silver-chalice">
                {lang === "de" 
                  ? "Verbinden Sie Ihre Website mit Google Analytics, um zu sehen, wie viele Besucher Sie haben." 
                  : "Connect your website with Google Analytics to see how many visitors you have."}
              </p>
            </div>
            
            <div>
              <label className="block text-sm text-white mb-1">
                Google Analytics ID
                <HelpTooltip text={lang === "de" 
                  ? "Diese ID finden Sie in Ihrem Google Analytics Konto unter 'Verwaltung > Property > Tracking-Info'. Sie beginnt mit 'G-' oder 'UA-'." 
                  : "Find this ID in your Google Analytics account under 'Admin > Property > Tracking Info'. It starts with 'G-' or 'UA-'."} 
                />
              </label>
              <input type="text" value={settings.google_analytics_id || ""} 
                onChange={(e) => setSettings({ ...settings, google_analytics_id: e.target.value })}
                placeholder="G-XXXXXXXXXX" 
                className="w-full max-w-md py-3 px-4 bg-black border border-mmg-mine-shaft text-white" 
              />
              <p className="text-xs text-mmg-silver-chalice mt-1">
                {lang === "de" ? "Lassen Sie das Feld leer, wenn Sie kein Google Analytics nutzen." : "Leave empty if you don't use Google Analytics."}
              </p>
            </div>
            
            <div className="pt-4 border-t border-mmg-mine-shaft">
              <label className="block text-sm text-white mb-1">
                Google Tag Manager ID
                <HelpTooltip text={lang === "de" 
                  ? "Nur für Fortgeschrittene: Wenn Sie Google Tag Manager nutzen, geben Sie hier die Container-ID ein." 
                  : "Advanced users only: If you use Google Tag Manager, enter the container ID here."} 
                />
              </label>
              <input type="text" value={settings.google_tag_manager_id || ""} 
                onChange={(e) => setSettings({ ...settings, google_tag_manager_id: e.target.value })}
                placeholder="GTM-XXXXXXX" 
                className="w-full max-w-md py-3 px-4 bg-black border border-mmg-mine-shaft text-white" 
              />
              <p className="text-xs text-mmg-silver-chalice mt-1">
                {lang === "de" ? "Optional - nur nötig wenn Sie GTM bereits eingerichtet haben." : "Optional - only needed if you've already set up GTM."}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* === TAB: KI & SPRACHSUCHE === */}
      {activeTab === 'ai' && (
        <div className="space-y-6">
          {/* Info Banner */}
          <div className="bg-blue-900/30 border border-blue-500/30 p-4 rounded">
            <h4 className="text-white font-semibold text-sm mb-1">
              {lang === "de" ? "Was ist KI-Optimierung?" : "What is AI Optimization?"}
            </h4>
            <p className="text-xs text-blue-200">
              {lang === "de" 
                ? "Immer mehr Menschen nutzen KI-Assistenten wie ChatGPT oder Siri, um Informationen zu finden. Mit diesen Einstellungen helfen Sie KI-Systemen, Ihre Website besser zu verstehen und zu empfehlen." 
                : "More and more people use AI assistants like ChatGPT or Siri to find information. These settings help AI systems better understand and recommend your website."}
            </p>
          </div>

          {/* FAQ Section - Completely redesigned */}
          <div className="bg-mmg-cod-gray border border-mmg-mine-shaft p-6 space-y-4">
            <div>
              <h3 className="font-heading text-lg text-white mb-1">
                {lang === "de" ? "Häufige Fragen (FAQ)" : "Frequently Asked Questions (FAQ)"}
                <HelpTooltip text={lang === "de" 
                  ? "Fügen Sie hier häufig gestellte Fragen und Antworten hinzu. Google zeigt diese oft direkt in den Suchergebnissen an!" 
                  : "Add frequently asked questions and answers here. Google often shows these directly in search results!"} 
                />
              </h3>
              <p className="text-xs text-mmg-silver-chalice">
                {lang === "de" 
                  ? "Diese Fragen können bei Google als 'Rich Snippets' erscheinen und mehr Klicks bringen." 
                  : "These questions can appear on Google as 'Rich Snippets' and bring more clicks."}
              </p>
            </div>
            
            {settings.faq_schema?.length === 0 && (
              <div className="text-center py-6 border-2 border-dashed border-mmg-mine-shaft rounded">
                <p className="text-mmg-silver-chalice text-sm mb-3">
                  {lang === "de" ? "Noch keine Fragen hinzugefügt" : "No questions added yet"}
                </p>
                <button onClick={addFaq} 
                  className="px-4 py-2 bg-white text-black font-heading font-bold uppercase text-sm hover:bg-black hover:text-white border border-white"
                  data-testid="add-first-faq-btn">
                  <Plus size={14} className="inline mr-2" />
                  {lang === "de" ? "Erste Frage hinzufügen" : "Add First Question"}
                </button>
              </div>
            )}
            
            {settings.faq_schema?.map((faq, index) => (
              <div key={index} className="bg-black p-4 border border-mmg-mine-shaft rounded space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-white font-semibold text-sm">
                    {lang === "de" ? `Frage ${index + 1}` : `Question ${index + 1}`}
                  </span>
                  <button onClick={() => removeFaq(index)} className="text-red-400 hover:text-red-300 text-xs flex items-center gap-1">
                    <Trash2 size={14} />
                    {lang === "de" ? "Entfernen" : "Remove"}
                  </button>
                </div>
                <div>
                  <label className="block text-xs text-mmg-silver-chalice mb-1">
                    {lang === "de" ? "Die Frage" : "The Question"}
                  </label>
                  <input type="text" value={faq.question} 
                    onChange={(e) => updateFaq(index, 'question', e.target.value)}
                    placeholder={lang === "de" ? "z.B. Was kostet eine Website?" : "e.g. How much does a website cost?"} 
                    className="w-full py-2 px-3 bg-mmg-cod-gray border border-mmg-mine-shaft text-white text-sm" 
                  />
                </div>
                <div>
                  <label className="block text-xs text-mmg-silver-chalice mb-1">
                    {lang === "de" ? "Ihre Antwort" : "Your Answer"}
                  </label>
                  <textarea value={faq.answer} 
                    onChange={(e) => updateFaq(index, 'answer', e.target.value)}
                    placeholder={lang === "de" ? "Geben Sie eine hilfreiche, ausführliche Antwort..." : "Give a helpful, detailed answer..."} 
                    className="w-full py-2 px-3 bg-mmg-cod-gray border border-mmg-mine-shaft text-white text-sm resize-none" 
                    rows="3" 
                  />
                </div>
              </div>
            ))}
            
            {settings.faq_schema?.length > 0 && (
              <button onClick={addFaq} className="text-mmg-silver-chalice hover:text-white text-sm flex items-center gap-1">
                <Plus size={14} />
                {lang === "de" ? "Weitere Frage hinzufügen" : "Add Another Question"}
              </button>
            )}
          </div>

          {/* Author Info - Simplified */}
          <div className="bg-mmg-cod-gray border border-mmg-mine-shaft p-6 space-y-4">
            <div>
              <h3 className="font-heading text-lg text-white mb-1">
                {lang === "de" ? "Über den Autor / Unternehmen" : "About the Author / Company"}
                <HelpTooltip text={lang === "de" 
                  ? "Diese Informationen helfen Suchmaschinen, Ihre Inhalte als vertrauenswürdig einzustufen." 
                  : "This information helps search engines classify your content as trustworthy."} 
                />
              </h3>
              <p className="text-xs text-mmg-silver-chalice">
                {lang === "de" ? "Optional: Zeigt Google, wer hinter dieser Website steht." : "Optional: Shows Google who is behind this website."}
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs text-mmg-silver-chalice mb-1">
                  {lang === "de" ? "Name (Person oder Firma)" : "Name (Person or Company)"}
                </label>
                <input type="text" value={settings.author_info?.name || ""} 
                  onChange={(e) => setSettings({ ...settings, author_info: { ...settings.author_info, name: e.target.value } })}
                  placeholder={lang === "de" ? "z.B. Mahzod Media Group" : "e.g. Mahzod Media Group"} 
                  className="w-full py-2 px-3 bg-black border border-mmg-mine-shaft text-white text-sm" 
                />
              </div>
              <div>
                <label className="block text-xs text-mmg-silver-chalice mb-1">
                  {lang === "de" ? "Position / Branche" : "Position / Industry"}
                </label>
                <input type="text" value={settings.author_info?.jobTitle || ""} 
                  onChange={(e) => setSettings({ ...settings, author_info: { ...settings.author_info, jobTitle: e.target.value } })}
                  placeholder={lang === "de" ? "z.B. Medienagentur" : "e.g. Media Agency"} 
                  className="w-full py-2 px-3 bg-black border border-mmg-mine-shaft text-white text-sm" 
                />
              </div>
            </div>
            
            <div>
              <label className="block text-xs text-mmg-silver-chalice mb-1">
                {lang === "de" ? "Website (falls anders als diese)" : "Website (if different from this one)"}
              </label>
              <input type="text" value={settings.author_info?.url || ""} 
                onChange={(e) => setSettings({ ...settings, author_info: { ...settings.author_info, url: e.target.value } })}
                placeholder="https://..." 
                className="w-full max-w-md py-2 px-3 bg-black border border-mmg-mine-shaft text-white text-sm" 
              />
            </div>
          </div>

          {/* Speakable/Voice - Hidden behind advanced toggle since it's very technical */}
          <details className="bg-mmg-cod-gray border border-mmg-mine-shaft rounded overflow-hidden">
            <summary className="p-4 cursor-pointer text-mmg-silver-chalice hover:text-white flex items-center justify-between">
              <span className="text-sm">
                {lang === "de" ? "Erweiterte Einstellungen (für Entwickler)" : "Advanced Settings (for developers)"}
              </span>
              <ChevronRight size={16} className="transform transition-transform" />
            </summary>
            <div className="p-4 border-t border-mmg-mine-shaft space-y-4">
              <div>
                <label className="block text-xs text-mmg-silver-chalice mb-1">
                  {lang === "de" ? "Sprachassistenten-Inhalte (CSS-Selektoren)" : "Voice Assistant Content (CSS Selectors)"}
                </label>
                <input type="text" value={settings.speakable_content?.cssSelector || ""} 
                  onChange={(e) => setSettings({ ...settings, speakable_content: { cssSelector: e.target.value } })}
                  placeholder=".speakable, h1, h2" 
                  className="w-full py-2 px-3 bg-black border border-mmg-mine-shaft text-white text-sm font-mono" 
                />
                <p className="text-xs text-mmg-silver-chalice mt-1">
                  {lang === "de" 
                    ? "Technisch: CSS-Selektoren für Inhalte, die von Sprachassistenten vorgelesen werden können." 
                    : "Technical: CSS selectors for content that can be read by voice assistants."}
                </p>
              </div>
              
              <div>
                <label className="block text-xs text-mmg-silver-chalice mb-1">robots.txt</label>
                <textarea value={settings.robots_txt || ""} 
                  onChange={(e) => setSettings({ ...settings, robots_txt: e.target.value })}
                  rows={4} 
                  className="w-full py-3 px-4 bg-black border border-mmg-mine-shaft text-white font-mono text-sm resize-none"
                  placeholder="User-agent: *&#10;Allow: /" 
                />
                <p className="text-xs text-mmg-silver-chalice mt-1">
                  {lang === "de" 
                    ? "Technisch: Regeln für Suchmaschinen-Crawler. Lassen Sie dies leer für Standardeinstellungen." 
                    : "Technical: Rules for search engine crawlers. Leave empty for default settings."}
                </p>
              </div>
            </div>
          </details>
        </div>
      )}

      {/* Save Button */}
      <div className="pt-4 border-t border-mmg-mine-shaft">
        <button onClick={handleSave} disabled={saving}
          className="px-8 py-3 bg-white text-black font-heading font-bold uppercase hover:bg-black hover:text-white border border-white disabled:opacity-50 transition-colors"
          data-testid="save-seo-btn">
          <Save size={16} className="inline mr-2" />
          {saving ? "..." : (lang === "de" ? "Alle Änderungen speichern" : "Save All Changes")}
        </button>
      </div>
    </div>
  );
};

const EDITORS = {
  hero: HeroEditor,
  about: AboutEditor,
  services: ServicesEditor,
  portfolio: PortfolioEditor,
  navigation: NavigationEditor,
  footer: FooterEditor,
  contact: ContactEditor,
};

// =================== MAIN COMPONENT ===================

const SiteManager = ({ token, lang }) => {
  const [siteContent, setSiteContent] = useState({});
  const [activeSection, setActiveSection] = useState(null);
  const [loading, setLoading] = useState(true);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchContent(); }, []);

  const fetchContent = async () => {
    try {
      const res = await axios.get(`${API}/site-content`, { headers: { Authorization: `Bearer ${token}` } });
      setSiteContent(res.data);
    } catch (e) { }
    setLoading(false);
  };

  const handleSave = async (section, data) => {
    try {
      await axios.put(`${API}/site-content/${section}`, data, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(lang === "de" ? "Gespeichert!" : lang === "ar" ? "تم الحفظ!" : "Saved!");
      fetchContent();
    } catch (e) { toast.error("Error saving"); }
  };

  if (loading) return <div className="text-mmg-silver-chalice">{lang === "de" ? "Laden..." : "Loading..."}</div>;

  // Special section views (Legal, Custom Pages, SEO)
  if (activeSection === "legal") {
    return (
      <div className="space-y-6" data-testid="site-editor-legal">
        <button onClick={() => setActiveSection(null)} className="flex items-center gap-2 text-mmg-silver-chalice hover:text-white" data-testid="back-to-site-hub">
          <ArrowLeft size={18} />{lang === "de" ? "Zurück zur Übersicht" : "Back to overview"}
        </button>
        <LegalEditor token={token} lang={lang} />
      </div>
    );
  }

  if (activeSection === "customPages") {
    return (
      <div className="space-y-6" data-testid="site-editor-custom-pages">
        <button onClick={() => setActiveSection(null)} className="flex items-center gap-2 text-mmg-silver-chalice hover:text-white" data-testid="back-to-site-hub">
          <ArrowLeft size={18} />{lang === "de" ? "Zurück zur Übersicht" : "Back to overview"}
        </button>
        <CustomPagesEditor token={token} lang={lang} />
      </div>
    );
  }

  if (activeSection === "seo") {
    return (
      <div className="space-y-6" data-testid="site-editor-seo">
        <button onClick={() => setActiveSection(null)} className="flex items-center gap-2 text-mmg-silver-chalice hover:text-white" data-testid="back-to-site-hub">
          <ArrowLeft size={18} />{lang === "de" ? "Zurück zur Übersicht" : "Back to overview"}
        </button>
        <SEOEditor token={token} lang={lang} />
      </div>
    );
  }

  // Section detail view
  if (activeSection) {
    const sec = SECTIONS.find(s => s.key === activeSection);
    const Editor = EDITORS[activeSection];
    const data = siteContent[activeSection] || {};
    return (
      <div className="space-y-6" data-testid={`site-editor-${activeSection}`}>
        <button onClick={() => setActiveSection(null)} className="flex items-center gap-2 text-mmg-silver-chalice hover:text-white" data-testid="back-to-site-hub">
          <ArrowLeft size={18} />{lang === "de" ? "Zurück zur Übersicht" : "Back to overview"}
        </button>
        <h1 className="font-heading font-bold text-3xl text-white">{sec?.label?.[lang] || sec?.label?.de || activeSection}</h1>
        {Editor && <Editor data={data} onSave={(d) => handleSave(activeSection, d)} token={token} />}
      </div>
    );
  }

  // Hub view
  const hubTitle = lang === "de" ? "Hauptseite verwalten" : lang === "ar" ? "إدارة الموقع الرئيسي" : "Manage Main Site";
  const hubDesc = lang === "de" ? "Bearbeiten Sie alle Inhalte der öffentlichen Website." : lang === "ar" ? "تحرير جميع محتويات الموقع العام." : "Edit all content of the public website.";

  return (
    <div className="space-y-6" data-testid="site-manager-hub">
      <h1 className="font-heading font-bold text-3xl text-white">{hubTitle}</h1>
      <p className="text-mmg-silver-chalice">{hubDesc}</p>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {SECTIONS.map((sec) => {
          const Icon = sec.icon;
          return (
            <button key={sec.key} onClick={() => setActiveSection(sec.key)}
              className="bg-mmg-cod-gray border border-mmg-mine-shaft p-6 text-left hover:border-white/30 transition-colors group"
              data-testid={`site-section-${sec.key}`}>
              <div className="flex items-center gap-3 mb-3">
                <Icon size={20} className="text-mmg-silver-chalice group-hover:text-white" />
                <h3 className="font-heading text-lg text-white">{sec.label?.[lang] || sec.label?.de}</h3>
              </div>
              <p className="text-sm text-mmg-silver-chalice">{sec.desc?.[lang] || sec.desc?.de}</p>
              <ChevronRight size={16} className="text-mmg-silver-chalice mt-3 group-hover:text-white" />
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default SiteManager;
